/**
 * The copy and the small pure logic of the profile connect code (`MEM-057`, ADR 053).
 *
 * Here rather than in the components for this app's usual reason: there is no DOM renderer in
 * `apps/web`'s test setup, so a sentence assembled inside JSX is a sentence with no test.
 */

/** §2.15's two controls, on the Your account header row. */
export const SHOW_MY_CODE_LABEL = 'Show my connection QR code';
export const SCAN_LABEL = 'Scan';

/**
 * What the header control says while the member is **at** an event (`MEM-057` AC-1).
 *
 * It names the evening, because the whole point of the branch is that this code is a different
 * code from the one the dialog would have shown — event-scoped, and closing when the event does.
 * A control that read *Show my connection QR code* and opened somewhere else would be the surprise
 * the branch exists to avoid.
 */
export function connectAtEventLabel(eventName: string): string {
  return `Connect at ${eventName}`;
}

/** §2.16's *my connection QR* overlay. The prototype's eyebrow, in its own words. */
export const MY_CODE_HEADING = 'Your connection code';
export const MY_CODE_SUPPORTING_LINE =
  'Have someone scan this to log the two of you as connected.';
export const MY_CODE_CLOSE_LABEL = 'Close';
export const MY_CODE_ALTERNATIVE_TEXT = 'Your H2HSpark connection code, as a QR code';

/**
 * The sentence that says what this code is **not**, and why it is on the dialog rather than in a
 * help affordance.
 *
 * A member who has used Connect at an event has seen a code that looked like this one and behaved
 * differently — it closed when the evening did, and the person who scanned it turned up in *People
 * you met*. This one has no event and no end. Ruling 6 made that the feature; saying so is what
 * stops it being a surprise.
 */
export const MY_CODE_WORKS_ANYWHERE =
  'This code works anywhere, not just at an event. Anyone who scans it is connected with you straight away — there is no request to accept.';

/**
 * `MEM-057` AC-2 — replacing the code, and the sentence that makes the consequence legible.
 *
 * **The control is on the dialog beside the code**, not behind a settings row. Replacement is the
 * only revocation this code has (ADR 053), and a revocation nobody can find is not one: the member
 * who needs it is the member looking at a code they think has got out.
 */
export const REPLACE_CODE_LABEL = 'Replace my code';
export const REPLACE_CODE_CONFIRM_LABEL = 'Yes, replace it';
export const REPLACE_CODE_BUSY_LABEL = 'Replacing…';
export const REPLACE_CODE_PROMPT = 'Replace this code?';
export const REPLACE_CODE_CONSEQUENCE =
  'The code you are showing now stops working, including any copy of it someone has photographed or saved. Connections you have already made are not affected.';
export const REPLACE_CODE_DONE = 'Replaced. The code above is the only one that works now.';

/** `MEM-057` AC-5 — what the desktop says instead of drawing a scanner. */
export const SCAN_NEEDS_A_PHONE_HEADING = 'Scanning needs your phone';
export const SCAN_NEEDS_A_PHONE_BODY =
  'Your code shows on any device, but reading someone else’s takes a camera you can point. Open H2HSpark on your phone and tap Scan there.';

/** `MEM-057` AC-6 — the notices, and the control that clears one. */
export const NOTICES_HEADING = 'New connections';
export const DISMISS_NOTICE_LABEL = 'Dismiss';

/** What a scan of someone else's profile code says when it worked. */
export function profileScanConnectedMessage(firstName: string): string {
  return `You're connected with ${firstName}.`;
}

/** And the link that follows it, because the connection is the only thing to do next. */
export function openConnectionLabel(firstName: string): string {
  return `Open ${firstName}'s connection`;
}
